import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, CircleDollarSign, Hash, Info, RefreshCw, Server } from 'lucide-react';
import { api, type SmsNumber } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { formatDateTime, formatNumber, formatPhone, formatUsd } from '../lib/format';
import { cn } from '../lib/utils';

/**
 * The estate viewed by shape rather than row by row — what the money is doing,
 * how the 10DLC registrations are distributed, and which numbers cannot do what
 * their row implies. Everything here comes from the same `GET /sms/numbers`
 * payload the inventory table uses, so the two can never disagree.
 */
export function SmsReport() {
  const numbers = useQuery({
    queryKey: ['sms', 'numbers'],
    queryFn: () => api.listSmsNumbers(),
  });

  const items = useMemo(() => numbers.data?.items ?? [], [numbers.data]);
  const summary = numbers.data?.summary;

  const report = useMemo(() => buildReport(items), [items]);
  const capacity = numbers.data?.capacity;
  const messageSpendCap = capacity?.spendLimits.find(
    (l) => l.name === 'TEXT_MESSAGE_MONTHLY_SPEND_LIMIT',
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Numbers report</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            What the 10DLC estate looks like in aggregate — spend, campaigns, and the numbers that
            cannot do what their row implies.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {numbers.data?.asOf && (
            <span className="text-xs text-muted-foreground">
              as of {formatDateTime(numbers.data.asOf)}
            </span>
          )}
          <Button
            variant="outline"
            onClick={() => void numbers.refetch()}
            disabled={numbers.isFetching}
          >
            <RefreshCw className={cn('h-4 w-4', numbers.isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {numbers.error && (
        <p className="text-sm text-destructive">{(numbers.error as Error).message}</p>
      )}
      {numbers.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          title="Monthly spend"
          icon={<CircleDollarSign className="h-4 w-4 text-muted-foreground" />}
          value={formatUsd(summary?.monthlyCost)}
          note={`${formatNumber(summary?.leased)} leased numbers`}
        />
        <Stat
          title="Earning their keep"
          icon={<Server className="h-4 w-4 text-muted-foreground" />}
          value={pct(summary?.assigned, summary?.leased)}
          note={`${formatNumber(summary?.assigned)} of ${formatNumber(summary?.leased)} assigned`}
        />
        <Stat
          title="Idle spend"
          icon={<Hash className="h-4 w-4 text-muted-foreground" />}
          value={formatUsd(summary?.spareMonthlyCost)}
          note={`${formatNumber(summary?.spare)} spare numbers`}
          tone={summary && summary.spare > 0 ? 'caution' : undefined}
        />
        <Stat
          title="Cannot send as expected"
          icon={<AlertTriangle className="h-4 w-4 text-muted-foreground" />}
          value={formatNumber(report.ineffective.length)}
          note="falling back to the shared number"
          tone={report.ineffective.length > 0 ? 'destructive' : undefined}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">By status</CardTitle>
          </CardHeader>
          <CardContent>
            <Bars
              rows={report.byStatus.map((r) => ({
                label: r.label,
                count: r.count,
                cost: r.cost,
              }))}
              total={items.length}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">By 10DLC registration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Bars
              rows={report.byRegistration.map((r) => ({
                label: r.registrationId,
                count: r.count,
                cost: r.cost,
                mono: true,
              }))}
              total={items.filter((i) => i.lease).length}
            />
            <p className="text-xs text-muted-foreground">
              A registration is a 10DLC campaign. Numbers sharing one share its carrier throughput
              and its vetting, so a campaign carrying most of the estate is a concentration worth
              knowing about.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <AlertTriangle
              className={cn(
                'h-4 w-4',
                report.ineffective.length > 0 ? 'text-destructive' : 'text-muted-foreground',
              )}
            />
            Numbers that cannot send as their row implies
          </CardTitle>
        </CardHeader>
        <CardContent>
          {report.ineffective.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing — every assigned number is leased, unique to its server, and on a running
              instance.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left">
                  <tr>
                    <th className="py-2 pr-4 font-medium text-muted-foreground">Number</th>
                    <th className="py-2 pr-4 font-medium text-muted-foreground">Customer</th>
                    <th className="py-2 pr-4 font-medium text-muted-foreground">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {report.ineffective.map((row) => (
                    <tr key={`${row.phoneNumber}-${row.reason}`} className="border-b border-border last:border-0">
                      <td className="py-2 pr-4 font-mono whitespace-nowrap">
                        {formatPhone(row.phoneNumber)}
                      </td>
                      <td className="py-2 pr-4">{row.customer || '—'}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Headroom</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-line-soft border-t border-line-soft text-sm">
            <Row
              label="Phone numbers leased"
              value={
                capacity?.phoneNumbers
                  ? `${formatNumber(capacity.phoneNumbers.used)} of ${formatNumber(capacity.phoneNumbers.max)}`
                  : '—'
              }
              note={
                capacity?.phoneNumbers && capacity.phoneNumbers.used >= capacity.phoneNumbers.max
                  ? 'at the account quota — no more can be leased until it is raised'
                  : 'account quota'
              }
              tone={
                capacity?.phoneNumbers && capacity.phoneNumbers.used >= capacity.phoneNumbers.max
                  ? 'caution'
                  : undefined
              }
            />
            <Row
              label="Unassigned and ready"
              value={formatNumber(summary?.spare)}
              note="spare numbers available to assign right now"
              tone={summary && summary.spare === 0 ? 'caution' : undefined}
            />
            {messageSpendCap && (
              <Row
                label="Monthly message spend cap"
                value={formatUsd(messageSpendCap.enforced)}
                note={`AWS stops sending above this; account maximum is ${formatUsd(messageSpendCap.max)}`}
              />
            )}
            {(capacity?.registrations ?? []).map((c) => (
              <Row
                key={c.registrationId}
                label={c.registrationId}
                value={formatNumber(c.numbersHeld)}
                note="numbers on this campaign"
              />
            ))}
          </dl>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Capability and configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="divide-y divide-line-soft border-t border-line-soft text-sm">
              <Row
                label="Two-way disabled"
                value={formatNumber(report.oneWay.length)}
                note="inbound replies are dropped"
                tone={report.oneWay.length > 0 ? 'caution' : undefined}
              />
              <Row label="MMS capable" value={formatNumber(report.mmsCapable)} />
              <Row label="Voice capable" value={formatNumber(report.voiceCapable)} />
              <Row
                label="Deletion protection off"
                value={formatNumber(report.unprotected)}
                note="can be released without a second step"
              />
              <Row label="Non-default opt-out list" value={formatNumber(report.customOptOut)} />
              <Row label="In a pool" value={formatNumber(report.pooled)} />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Estate and tenure</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="divide-y divide-line-soft border-t border-line-soft text-sm">
              <Row label="Servers with a number" value={formatNumber(summary?.servers)} />
              <Row label="Distinct customers" value={formatNumber(summary?.customers)} />
              <Row
                label="On a stopped server"
                value={formatNumber(summary?.stoppedServers)}
                note="paid for, cannot send"
                tone={summary && summary.stoppedServers > 0 ? 'caution' : undefined}
              />
              <Row
                label="Server no longer exists"
                value={formatNumber(summary?.unresolvedServers)}
                note="stale registry row"
                tone={summary && summary.unresolvedServers > 0 ? 'caution' : undefined}
              />
              <Row
                label="Servers with >1 number"
                value={formatNumber(summary?.multiNumberServers)}
                note="must be 1:1 — these fall back to the default"
                tone={summary && summary.multiNumberServers > 0 ? 'caution' : undefined}
              />
              <Row label="Oldest lease" value={formatDateTime(report.oldestLease)} />
              <Row label="Newest lease" value={formatDateTime(report.newestLease)} />
              <Row label="Countries" value={report.countries.join(', ')} />
            </dl>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Info className="h-4 w-4 text-muted-foreground" />
            Reading this report
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <span className="text-foreground">Cannot send as their row implies</span> is the list
            worth acting on. Each of these servers looks configured but its alarms actually leave
            from the shared fallback number — because the number is not leased, because the server
            holds more than one row (the gateway returns the default when its lookup finds two), or
            because the instance is not running.
          </p>
          <p>
            <span className="text-foreground">Idle spend</span> is expected headroom for onboarding,
            not a fault. It is here so the size of it stays visible.
          </p>
          <p>
            Every figure is derived from the same payload as the{' '}
            <Link to="/sms/numbers" className="text-accent hover:underline">
              inventory table
            </Link>
            , so the two cannot drift apart.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

interface IneffectiveRow {
  phoneNumber: string;
  customer: string;
  reason: string;
}

function buildReport(items: SmsNumber[]) {
  const leased = items.filter((i) => i.lease);
  const costOf = (list: SmsNumber[]) =>
    list.reduce((total, i) => total + (i.lease?.monthlyPrice ?? 0), 0);

  const statusOrder: { key: SmsNumber['status']; label: string }[] = [
    { key: 'assigned', label: 'Assigned' },
    { key: 'shared', label: 'Shared across a customer’s servers' },
    { key: 'spare', label: 'Spare' },
    { key: 'default', label: 'Shared fallback' },
    { key: 'conflicted', label: 'Server holds another number' },
    { key: 'duplicate', label: 'Claimed by two customers' },
    { key: 'unleased', label: 'Not leased' },
  ];
  const byStatus = statusOrder
    .map(({ key, label }) => {
      const rows = items.filter((i) => i.status === key);
      return { label, count: rows.length, cost: costOf(rows) };
    })
    .filter((r) => r.count > 0);

  const registrations = new Map<string, SmsNumber[]>();
  for (const item of leased) {
    const id = item.lease?.registrationId ?? 'none';
    registrations.set(id, [...(registrations.get(id) ?? []), item]);
  }
  const byRegistration = [...registrations.entries()]
    .map(([registrationId, rows]) => ({
      registrationId,
      count: rows.length,
      cost: costOf(rows),
    }))
    .sort((a, b) => b.count - a.count);

  // The single actionable list: an assigned number whose customer is not
  // actually sending from it. Each cause produces the same visible outcome —
  // messages leave from the shared fallback.
  const ineffective: IneffectiveRow[] = [];
  for (const item of items) {
    const customer = item.assignments.map((a) => a.customer).join(', ');
    if (item.status === 'unleased') {
      ineffective.push({
        phoneNumber: item.phoneNumber,
        customer,
        reason: 'not a number this account leases',
      });
      continue;
    }
    if (item.status === 'conflicted') {
      ineffective.push({
        phoneNumber: item.phoneNumber,
        customer,
        reason: `server also holds ${item.conflictsWith.join(', ')} — the gateway uses neither`,
      });
      continue;
    }
    if (item.status === 'default' && item.assignments.length > 0) {
      ineffective.push({
        phoneNumber: item.phoneNumber,
        customer,
        reason: 'the shared fallback must not be assigned to one customer',
      });
      continue;
    }
    if (item.status === 'duplicate') {
      ineffective.push({
        phoneNumber: item.phoneNumber,
        customer,
        reason: 'claimed by two customers — replies cannot be attributed',
      });
      continue;
    }
    for (const a of item.assignments) {
      if (a.server && a.server.state !== 'running') {
        ineffective.push({
          phoneNumber: item.phoneNumber,
          customer: a.customer,
          reason: `server is ${a.server.state}`,
        });
      } else if (!a.server) {
        ineffective.push({
          phoneNumber: item.phoneNumber,
          customer: a.customer,
          reason: 'no EC2 instance holds the registered address',
        });
      }
    }
  }

  const leaseDates = leased
    .map((i) => i.lease?.createdAt)
    .filter((d): d is string => typeof d === 'string')
    .sort();

  return {
    byStatus,
    byRegistration,
    ineffective,
    oneWay: leased.filter((i) => i.lease?.twoWayEnabled === false),
    mmsCapable: leased.filter((i) => i.lease?.capabilities.includes('MMS')).length,
    voiceCapable: leased.filter((i) => i.lease?.capabilities.includes('VOICE')).length,
    unprotected: leased.filter((i) => i.lease?.deletionProtection === false).length,
    customOptOut: leased.filter(
      (i) => i.lease?.optOutListName && i.lease.optOutListName !== 'Default',
    ).length,
    pooled: leased.filter((i) => i.lease?.poolId).length,
    oldestLease: leaseDates[0] ?? null,
    newestLease: leaseDates[leaseDates.length - 1] ?? null,
    countries: [
      ...new Set(
        leased.map((i) => i.lease?.isoCountryCode).filter((c): c is string => typeof c === 'string'),
      ),
    ].sort(),
  };
}

function pct(part: number | undefined, whole: number | undefined): string {
  if (!part || !whole) return '—';
  return `${Math.round((part / whole) * 100)}%`;
}

function Bars({
  rows,
  total,
}: {
  rows: { label: string; count: number; cost: number; mono?: boolean }[];
  total: number;
}) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">No data.</p>;
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.label}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className={cn('min-w-0 truncate', r.mono && 'font-mono text-xs')} title={r.label}>
              {r.label}
            </span>
            <span className="shrink-0 tabular-nums">
              {formatNumber(r.count)}
              <span className="text-xs text-muted-foreground">
                {total > 0 ? ` · ${Math.round((r.count / total) * 100)}%` : ''} ·{' '}
                {formatUsd(r.cost)}
              </span>
            </span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-raise">
            <div
              className="h-full rounded-full bg-accent"
              style={{ width: `${Math.max(2, Math.round((r.count / max) * 100))}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function Stat({
  title,
  icon,
  value,
  note,
  tone,
}: {
  title: string;
  icon: React.ReactNode;
  value: string;
  note: string;
  tone?: 'caution' | 'destructive';
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="truncate text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            'text-2xl font-bold tabular-nums',
            tone === 'destructive' && 'text-destructive',
            tone === 'caution' && 'text-caution',
          )}
        >
          {value}
        </div>
        <p className="text-xs text-muted-foreground">{note}</p>
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'caution';
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <dt className="text-muted-foreground">
        {label}
        {note && <span className="block text-xs text-muted-foreground/70">{note}</span>}
      </dt>
      <dd className={cn('shrink-0 tabular-nums', tone === 'caution' && 'text-caution')}>
        {value === '' ? '—' : value}
      </dd>
    </div>
  );
}
